import { useEffect, useState } from "react";
import type { ParticleType } from "./game/constants";

export type Locale = "en" | "zh";

export function getLocaleFromPath(pathname: string): Locale {
  if (pathname === "/zh" || pathname.startsWith("/zh/")) return "zh";
  return "en";
}

export function useLocale(): Locale {
  const [locale, setLocale] = useState<Locale>(() =>
    typeof window === "undefined" ? "en" : getLocaleFromPath(window.location.pathname),
  );

  useEffect(() => {
    const update = () => setLocale(getLocaleFromPath(window.location.pathname));
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);

  return locale;
}

const translations = {
  en: {
    moreMaterials: "More materials",
    moreTools: "More tools",
    glow: "Glow",
    clear: "Clear",
    leftClick: "Left click",
    draw: "Draw",
    wheel: "Wheel",
    size: "Size",
    cmdCtrl: "Cmd/Ctrl",
    line: "Line",
    cmdShift: "Cmd+Shift",
    rect: "Rectangle",
    enableGravity: "Enable gravity",
    disableGravity: "Disable gravity",
    localSim: "Local sim",
    simMouse: "Sim (mouse)",
    brushSize: "Brush size",
  },
  zh: {
    moreMaterials: "更多材质",
    moreTools: "更多工具",
    glow: "发光",
    clear: "清空",
    leftClick: "左键",
    draw: "绘制",
    wheel: "滚轮",
    size: "大小",
    cmdCtrl: "Cmd/Ctrl",
    line: "直线",
    cmdShift: "Cmd+Shift",
    rect: "矩形",
    enableGravity: "开启重力",
    disableGravity: "关闭重力",
    localSim: "本地模拟",
    simMouse: "模拟中(鼠标)",
    brushSize: "笔刷大小",
  },
} as const;

export function t(locale: Locale, key: keyof (typeof translations)["en"]): string {
  return translations[locale][key];
}

const enToolLabels: Record<ParticleType, string> = {
  bedrock: "Bedrock",
  sand: "Sand",
  water: "Water",
  oil: "Oil",
  steam: "Steam",
  ice: "Ice",
  gunpowder: "Gunpowder",
  saltpeter: "Saltpeter",
  sulfur: "Sulfur",
  stone: "Stone",
  gravel: "Gravel",
  wood: "Wood",
  fire: "Fire",
  lava: "Lava",
  smoke: "Smoke",
  charcoal: "Charcoal",
  eraser: "Eraser",
  mixer: "Mixer",
  drag: "Drag",
  heater: "Heater",
  crusher: "Crusher",
  glue: "Glue",
  cooler: "Cooler",
  filter: "Filter",
};

export function getToolLabel(locale: Locale, type: ParticleType, fallbackLabel: string): string {
  if (locale === "zh") return fallbackLabel;
  return enToolLabels[type] ?? fallbackLabel;
}

const enHoverNameOverrides: Record<string, string> = {
  heater: "Heater",
  cooler: "Cooler",
  crusher: "Crusher",
  glue: "Glue",
  drag: "Drag",
};

export function getHoverName(locale: Locale, rawName: string, zhName: string): string {
  if (locale === "zh") return zhName;
  return enHoverNameOverrides[rawName] ?? rawName;
}

